import type { ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { setCurrentIdToken, setSignInTrigger, setSignOutTrigger, setUserEmail } from "./auth";
import { config, isOidcConfigured } from "./config";

/**
 * Gates the app behind OIDC sign-in when configured.
 *
 * Local dev: when `cognitoAuthority` is empty, OIDC is skipped entirely and
 * the dev-mode `X-Dev-User` header continues to authenticate. Deployed envs:
 * unauthenticated visitors see a sign-in card; authenticated visitors get the
 * children. On mount we also publish the current ID token + sign-in trigger
 * to the module-scoped store in `auth.ts` so api.ts can read them without
 * needing React context.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  if (!isOidcConfigured()) {
    return <>{children}</>;
  }
  return <OidcGate>{children}</OidcGate>;
}

function OidcGate({ children }: { children: ReactNode }) {
  const auth = useAuth();

  // Treat past-expiry tokens as unauthenticated even if the library still
  // has the user cached. Otherwise the SPA renders the chat UI with a stale
  // token, every API call 401s, and the user has to reload to be bounced
  // to the IdP. With this, the gate flips to the sign-in card right away.
  const tokenLive = !auth.user || isTokenLive(auth.user);
  const effectiveAuthed = auth.isAuthenticated && tokenLive;

  // Mirror auth state into the module store synchronously during render so
  // api.ts sees the token before App's mount effects fire. (React commits
  // child effects before parent effects, so doing this in a useEffect lets
  // App's first fetch race ahead of the token cache, hit /api with no
  // Authorization header, get 401, and trigger a sign-in loop.)
  setCurrentIdToken(effectiveAuthed ? (auth.user?.id_token ?? null) : null);
  setUserEmail((auth.user?.profile.email as string | undefined) ?? null);
  setSignInTrigger(() => auth.signinRedirect());
  setSignOutTrigger(() => auth.signoutRedirect());

  if (auth.isLoading) {
    return (
      <div className="auth-status">
        <p>Signing you in…</p>
      </div>
    );
  }
  if (auth.error) {
    return (
      <div className="auth-status auth-status--error">
        <p>Sign-in failed: {auth.error.message}</p>
        <button type="button" onClick={() => auth.signinRedirect()}>
          Try again
        </button>
      </div>
    );
  }
  if (!effectiveAuthed) {
    return (
      <div className="auth-status">
        <h1>{config.appName}</h1>
        <p>This tool is restricted to {config.cognitoSamlIdpName || "IIC"} users.</p>
        <button type="button" onClick={() => auth.signinRedirect()}>
          Sign in with {config.cognitoSamlIdpName || "Identity Center"}
        </button>
      </div>
    );
  }
  return <>{children}</>;
}

function isTokenLive(user: { expires_at?: number }): boolean {
  // expires_at is unix seconds. Add a small skew so a token that expires in
  // the next few seconds is treated as already gone — we'd rather show the
  // sign-in card than ship a stale Authorization header.
  if (!user.expires_at) return true;
  return user.expires_at - 5 > Math.floor(Date.now() / 1000);
}
